import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Grid, Tooltip } from '@mui/material';
import { DataGridPro, GridColDef, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSnackbar } from 'notistack';
import PortalRoleChips from '../PortalRoleChips';
import DirectoryStatusChip from '../DirectoryStatusChip';
import CompanyUser from 'store/types/CompanyUser';
import { DirectoryListing, DirectoryNetwork, UpdateDirectoryListing } from 'store/types/DirectoryListing';
import DirectoryListingService from 'services/api/DirectoryListingService';
import { EMPTY_STRING_VALUE, getUserFullName } from 'util/Format';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';

interface DirectoryListingsTableProps {
  companyId: string;
  network: DirectoryNetwork;
  companyUsers?: CompanyUser[];
  refetch: () => void | Promise<unknown>;
}

type DirectoryListingRow = DirectoryListing & {
  id: string;
  name: string;
  email: string;
  title: string;
  functions: NonNullable<CompanyUser['functions']>;
  __reorder__: string;
};

interface DirectoryListingTableState {
  rows: DirectoryListingRow[];
  originalRows: DirectoryListingRow[];
}

const visibleIcon = <VisibilityIcon color={'primary'} fontSize={'small'} />;

const hiddenIcon = <VisibilityOffIcon color={'error'} fontSize={'small'} />;

const createRows = (network: DirectoryNetwork, companyUsers: CompanyUser[] = []): DirectoryListingRow[] => {
  const usersById = new Map(companyUsers.map((user) => [String(user.id), user]));

  return [...(network.listings ?? [])]
    .sort((first, second) => first.sequence - second.sequence)
    .map((listing) => {
      const user = usersById.get(String(listing.userId));
      const name = user ? getUserFullName(user, true) : '';

      return {
        ...listing,
        id: listing.directoryListingId,
        name,
        email: user?.email ?? '',
        title: user?.title ?? '',
        functions: user?.functions ?? [],
        __reorder__: name || listing.userId,
      };
    });
};

const DirectoryListingsTable: React.FunctionComponent<DirectoryListingsTableProps> = ({
  companyId,
  network,
  companyUsers = [],
  refetch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);

  const [tableState, setTableState] = useState<DirectoryListingTableState>(() => {
    const initialRows = createRows(network, companyUsers);

    return {
      rows: initialRows,
      originalRows: initialRows,
    };
  });

  const { rows, originalRows } = tableState;

  const hasChanges = useMemo(() => {
    if (rows.length !== originalRows.length) {
      return true;
    }

    const originalRowsById = new Map(originalRows.map((row) => [row.id, row]));

    return rows.some((row) => {
      const original = originalRowsById.get(row.id);

      return !original || original.sequence !== row.sequence || original.hideFromDirectory !== row.hideFromDirectory;
    });
  }, [originalRows, rows]);

  const handleHideFromDirectoryChange = useCallback((directoryListingId: string, hideFromDirectory: boolean) => {
    setTableState((currentState) => ({
      ...currentState,
      rows: currentState.rows.map((row) =>
        row.id === directoryListingId
          ? {
              ...row,
              hideFromDirectory,
            }
          : row
      ),
    }));
  }, []);

  const handleRowOrderChange = useCallback((params: GridRowOrderChangeParams) => {
    setTableState((currentState) => {
      const reorderedRows = [...currentState.rows];
      const [movedRow] = reorderedRows.splice(params.oldIndex, 1);

      if (!movedRow) {
        return currentState;
      }

      reorderedRows.splice(params.targetIndex, 0, movedRow);

      return {
        ...currentState,
        rows: reorderedRows.map((row, index) => ({
          ...row,
          sequence: index + 1,
        })),
      };
    });
  }, []);

  const handleReset = useCallback(() => {
    setTableState((currentState) => ({
      ...currentState,
      rows: currentState.originalRows,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      return;
    }

    const listings: UpdateDirectoryListing[] = rows.map((row) => ({
      directoryListingId: row.directoryListingId,
      sequence: row.sequence,
      hideFromDirectory: row.hideFromDirectory,
    }));

    setSaving(true);

    try {
      await DirectoryListingService.updateDirectoryListings(companyId, network.directoryNetworkId, listings);

      setTableState((currentState) => ({
        ...currentState,
        originalRows: currentState.rows,
      }));

      enqueueSnackbar(`${network.directoryNetworkName} listings successfully updated`, {
        variant: 'success',
      });

      await refetch();
    } catch (errorMessage) {
      enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
    } finally {
      setSaving(false);
    }
  }, [companyId, enqueueSnackbar, hasChanges, network.directoryNetworkId, network.directoryNetworkName, refetch, rows]);

  const columns = useMemo<GridColDef<DirectoryListingRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: false,
      },
      {
        field: 'title',
        headerName: 'Job Title',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => params.row.title || EMPTY_STRING_VALUE,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 220,
        sortable: false,
      },
      {
        field: 'functions',
        headerName: 'Portal Role',
        flex: 1.5,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => <PortalRoleChips functions={params.row.functions} />,
      },
      {
        field: 'directoryStatusName',
        headerName: 'Directory Status',
        minWidth: 170,
        flex: 1,
        sortable: false,
        cellClassName: 'directory-status-cell',
        renderCell: (params) => <DirectoryStatusChip status={params.row.directoryStatusName} />,
      },
      {
        field: 'hideFromDirectory',
        headerName: 'Directory Visibility',
        minWidth: 180,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        cellClassName: 'directory-visibility-cell',
        renderCell: (params) => {
          const hidden = Boolean(params.value);

          return (
            <Tooltip title={hidden ? 'Hidden from directory' : 'Visible in directory'}>
              <span>
                <Checkbox
                  icon={hiddenIcon}
                  checkedIcon={visibleIcon}
                  checked={!hidden}
                  disabled={saving}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(_event, checked) => {
                    handleHideFromDirectoryChange(params.row.directoryListingId, !checked);
                  }}
                />
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [handleHideFromDirectoryChange, saving]
  );

  return (
    <Grid {...defaultGridContainerProps} spacing={2}>
      <Grid {...defaultGridItemProps}>
        <h3 className={commonStyles.subTitle}>{network.directoryNetworkName}</h3>
      </Grid>

      {!rows.length ? (
        <Grid {...defaultGridItemProps}>
          <Alert severity={'info'}>{'There are no listings for this directory network.'}</Alert>
        </Grid>
      ) : (
        <Grid {...defaultGridItemProps}>
          <Box
            sx={{
              width: '100%',
              maxHeight: 650,
              overflowY: 'auto',
            }}
          >
            <DataGridPro
              sx={{
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
                '& .directory-row-hidden .MuiDataGrid-cell': {
                  color: 'text.disabled',
                  opacity: 0.8,
                  transition: 'opacity 150ms ease',
                },
                '& .directory-row-hidden .directory-status-cell, & .directory-row-hidden .directory-visibility-cell': {
                  color: 'inherit',
                  opacity: 1,
                },
              }}
              autoHeight
              rows={rows}
              columns={columns}
              loading={saving}
              rowHeight={60}
              rowReordering
              disableColumnReorder
              disableColumnResize
              disableColumnMenu
              disableSelectionOnClick
              disableColumnFilter
              getRowClassName={(params) => (params.row.hideFromDirectory ? 'directory-row-hidden' : '')}
              onRowOrderChange={handleRowOrderChange}
            />
          </Box>
        </Grid>
      )}

      {hasChanges && (
        <Grid {...defaultGridItemProps}>
          <Alert
            sx={{
              width: '100%',
              alignItems: 'center',
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            severity={'warning'}
            icon={false}
            action={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Button
                  sx={{
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'grey.50',
                    },
                  }}
                  color={'secondary'}
                  variant={'outlined'}
                  size={'small'}
                  disabled={saving}
                  onClick={handleReset}
                >
                  {'Reset'}
                </Button>

                <Button color={'secondary'} variant={'contained'} size={'small'} disabled={saving} onClick={handleSave}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            }
          >
            {'You have unsaved changes to the directory listing.'}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default DirectoryListingsTable;
